import { FC, ReactNode, useMemo } from "react"
import { Check } from "lucide-react"
import { useIntegrationSettingsForm } from "../../../../hooks"
import { useAppSelector } from "../../../../hooks"
import { selectUserDetails } from "../../../../../redux/slices/auth"
import {
  useFetchLoanOfficerQuery,
  useFetchOrgDetailsQuery
} from "../../../../../redux/queries"
import { getUserBrandId } from "../../../../utils"
import { Integration } from "../../../../../types"
import LeadWorkflowSelector from "../lead-workflow-selector"
import ContactFormSelector from "../contact-form-selector"
import RateCardsSelector from "../rate-cards-selector"
import RateTableSelector from "../rate-table-selector"

interface ModuleTileProps {
  id: string
  label: string
  desc: string
  checked: boolean
  onChange: () => void
  subSelector?: ReactNode
}

const ModuleTile: FC<ModuleTileProps> = ({
  label,
  desc,
  checked,
  onChange,
  subSelector
}) => (
  <div
    className={[
      "rounded border transition-all",
      checked
        ? "bg-primary-50/40 border-primary-200"
        : "bg-transparent border-transparent hover:border-neutral-200"
    ].join(" ")}
  >
    <div
      onClick={onChange}
      className="flex items-start gap-3 p-3.5 cursor-pointer select-none"
    >
      <div
        className={[
          "flex-shrink-0 mt-0.5 rounded-[3px] border flex items-center justify-center transition-all",
          checked
            ? "bg-primary-500 border-primary-500"
            : "bg-white border-neutral-300"
        ].join(" ")}
        style={{ width: 18, height: 18 }}
      >
        {checked && <Check size={10} className="text-white" strokeWidth={3} />}
      </div>
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-neutral-800 leading-tight">
          {label}
        </div>
        <div className="text-[11px] text-neutral-400 mt-0.5">{desc}</div>
      </div>
    </div>
    {subSelector && (
      <div className="px-3.5 pb-3.5 border-t border-primary-100 pt-3">
        {subSelector}
      </div>
    )}
  </div>
)

interface Props {
  integration?: Integration
}

const WidgetsCheckboxes = ({ integration }: Props) => {
  const { app_settings, saveAppSetting, saveSetting, values } =
    useIntegrationSettingsForm(integration)

  const { settings } = app_settings ?? {}
  const user = useAppSelector(selectUserDetails)
  const ownerId = values.owner || user?.id || ""
  const isCurrentUser = ownerId === user?.id
  const needsOwnerFetch = !integration?.brand_id && !isCurrentUser && !!ownerId

  const { data: ownerData } = useFetchLoanOfficerQuery(ownerId, {
    skip: !needsOwnerFetch
  })
  const { data: ownerOrg } = useFetchOrgDetailsQuery(
    { id: String(ownerData?.org_id ?? "") },
    { skip: !needsOwnerFetch || !ownerData?.org_id }
  )

  const brandId = useMemo(() => {
    if (integration?.brand_id) return integration.brand_id
    if (values.brand_id) return values.brand_id
    const orgToUse = isCurrentUser ? user?.org : ownerOrg
    if (orgToUse && ownerId) {
      const fallbackBrandId = getUserBrandId(orgToUse, ownerId)
      return fallbackBrandId ?? orgToUse?.brands?.[0]?.id
    }
    return undefined
  }, [
    integration?.brand_id,
    values.brand_id,
    isCurrentUser,
    user?.org,
    ownerOrg,
    ownerId
  ])

  const integrationWithBrandId = useMemo(() => {
    const base = integration ?? values
    if (!base) return undefined
    return { ...base, brand_id: brandId ?? base.brand_id ?? "" }
  }, [integration, values, brandId])

  // ── Derived checked states ──────────────────────────────────────────────
  const mortCalc = app_settings?.mort_calc === 1
  const affordCalc = app_settings?.afford_calc === 1
  const leadWorkflow =
    !!app_settings?.lead_workflow && app_settings.lead_workflow >= 1
  const pricing = app_settings?.pricing === 1
  const contactForm = !!(settings?.contactForm && settings.contactForm > 0)
  const rateTable = settings?.rateTable ?? false
  const loReviews = settings?.loReviews ?? false
  const brandReviews = settings?.brandReviews ?? false
  const aboutBoxes = settings?.aboutBoxes ?? false

  // ── Module definitions ──────────────────────────────────────────────────
  const modules: ModuleTileProps[] = [
    // First rows: All modules with dropdowns
    {
      id: "lead_workflow",
      label: "Lead Workflow",
      desc: "CRM data bridge",
      checked: leadWorkflow,
      onChange: () => saveAppSetting({ lead_workflow: leadWorkflow ? 0 : 1 }),
      subSelector:
        leadWorkflow && integrationWithBrandId ? (
          <LeadWorkflowSelector integration={integrationWithBrandId} />
        ) : undefined
    },
    {
      id: "rateTable",
      label: "Rate Table",
      desc: "Product comparison matrix",
      checked: rateTable,
      onChange: () => saveSetting({ rateTable: !rateTable }),
      subSelector:
        rateTable && integration ? (
          <RateTableSelector integration={integration} />
        ) : undefined
    },
    {
      id: "pricing",
      label: "Pricing Cards",
      desc: "Real-time market rates",
      checked: pricing,
      onChange: () => {
        const next = pricing ? 0 : 1
        saveAppSetting({
          pricing: next,
          settings: {
            ...settings,
            rateCards: !!next,
            ...(pricing && { rateCardsConfigId: null })
          }
        })
      },
      subSelector:
        pricing && integrationWithBrandId ? (
          <RateCardsSelector integration={integrationWithBrandId} />
        ) : undefined
    },
    {
      id: "contactForm",
      label: "Contact Form",
      desc: "Inquiry collection",
      checked: contactForm,
      onChange: () => saveSetting({ contactForm: contactForm ? 0 : 1 }),
      subSelector:
        contactForm && integrationWithBrandId ? (
          <ContactFormSelector integration={integrationWithBrandId} />
        ) : undefined
    },
    // Remaining modules without dropdowns
    {
      id: "mort_calc",
      label: "Mortgage Calculator",
      desc: "Estimate monthly payments",
      checked: mortCalc,
      onChange: () => saveAppSetting({ mort_calc: mortCalc ? 0 : 1 })
    },
    {
      id: "afford_calc",
      label: "Affordability Calculator",
      desc: "Dynamic purchasing power",
      checked: affordCalc,
      onChange: () => saveAppSetting({ afford_calc: affordCalc ? 0 : 1 })
    },
    {
      id: "loReviews",
      label: "LO Reviews",
      desc: "Loan officer online reviews",
      checked: loReviews,
      onChange: () => saveSetting({ loReviews: !loReviews })
    },
    {
      id: "brandReviews",
      label: "Brand Reviews",
      desc: "Corporate reputation",
      checked: brandReviews,
      onChange: () => saveSetting({ brandReviews: !brandReviews })
    },
    {
      id: "aboutBoxes",
      label: "About Boxes",
      desc: "Mission & team bios",
      checked: aboutBoxes,
      onChange: () => saveSetting({ aboutBoxes: !aboutBoxes })
    }
  ]

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {modules.map(mod => (
        <ModuleTile key={mod.id} {...mod} />
      ))}
    </div>
  )
}

export default WidgetsCheckboxes
